import type { BadgeTone } from '@/components/ui/badge-tones';

import type { TimesheetStateCode } from './types';

/**
 * Teinte d'un état, par **code** et non par libellé.
 *
 * Le référentiel est administrable : un état renommé perdrait sa couleur si la
 * correspondance portait sur le texte.
 */
export const STATE_TONES: Record<string, BadgeTone> = {
  BROUILLON: 'muted',
  SOUMIS: 'warning',
  VALIDE: 'success',
  REJETE: 'danger',
};

/**
 * Ce que chaque état dit **au consultant**.
 *
 * Les mêmes codes servent à l'outil interne, où ils se lisent autrement : là
 * « soumis » signifie « à valider », ici « en attente du retour de ubeefy ».
 * Un libellé partagé aurait parlé au mauvais lecteur.
 */
export const STATE_HINTS: Record<TimesheetStateCode, string> = {
  BROUILLON: 'À compléter et à envoyer',
  SOUMIS: 'Envoyé — en attente de validation',
  VALIDE: 'Validé par ubeefy',
  REJETE: 'À corriger et à renvoyer',
};

/** Couleur de fond d'une journée, par nature. */
export const DAY_TONES: Record<string, string> = {
  TRAVAILLE: 'bg-accent-50 text-accent-800 border-accent-200',
  CONGE: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  RTT: 'bg-sky-50 text-sky-800 border-sky-200',
  MALADIE: 'bg-amber-50 text-amber-800 border-amber-200',
  FERIE: 'bg-navy-100 text-navy-700 border-navy-200',
  ABSENCE: 'bg-red-50 text-red-800 border-red-200',
};

export const dayTone = (code: string): string =>
  DAY_TONES[code] ?? 'bg-navy-50 text-navy-700 border-navy-200';

/** Mois `AAAA-MM` en toutes lettres : « septembre 2026 ». */
export const formatMonth = (value: string): string => {
  const [year, month] = value.split('-');
  if (!year || !month) return value;

  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
};

/** Mois courant au format `AAAA-MM`, dans le fuseau du navigateur. */
export const currentMonth = (): string => {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Mois décalé de `offset` mois par rapport à un `AAAA-MM`. */
export const shiftMonth = (periodMonth: string, offset: number): string => {
  const [year, month] = periodMonth.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 + offset, 1));

  return date.toISOString().slice(0, 7);
};

/** Une case du calendrier mensuel. */
export interface CalendarCell {
  /** `AAAA-MM-JJ`. */
  date: string;
  dayOfMonth: number;
  /** Samedi ou dimanche. */
  isWeekend: boolean;
  /** Hors des bornes de la mission : rien ne peut y être déclaré. */
  isOutsideMission: boolean;
}

/**
 * Grille du mois, semaine par semaine, lundi en premier.
 *
 * Les cases vides du début et de la fin sont des `null` : elles occupent la
 * place sans être cliquables, ce qui garde les colonnes alignées sur les jours
 * de la semaine — un mois qui commence un jeudi ne doit pas décaler tout le
 * tableau.
 */
export const buildCalendar = (
  periodMonth: string,
  missionStart: string,
  missionEnd: string | null,
): (CalendarCell | null)[][] => {
  const [year, month] = periodMonth.split('-').map(Number);
  if (!year || !month) return [];

  const total = new Date(Date.UTC(year, month, 0)).getUTCDate();

  /* `getUTCDay()` rend 0 pour dimanche : on le ramène en fin de semaine, comme
     un calendrier français. */
  const firstWeekday = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;

  const cells: (CalendarCell | null)[] = Array.from({ length: firstWeekday }, () => null);

  for (let day = 1; day <= total; day += 1) {
    const date = new Date(Date.UTC(year, month - 1, day));
    const iso = date.toISOString().slice(0, 10);
    const weekday = date.getUTCDay();

    cells.push({
      date: iso,
      dayOfMonth: day,
      isWeekend: weekday === 0 || weekday === 6,
      isOutsideMission: iso < missionStart || (missionEnd !== null && iso > missionEnd),
    });
  }

  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (CalendarCell | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }

  return weeks;
};

export const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Nombre en français : « 2,5 » plutôt que « 2.50 ». */
export const formatDays = (value: string): string => {
  const days = Number(value);
  if (Number.isNaN(days)) return value;

  return days.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
};
